import {
  items,
  when,
  required,
  shape,
  concatFailure,
  unique,
  logTo,
  fromRoot,
  atLeastOne,
  lessThan,
  greaterThan,
  fromModel,
  stopOnFirstFailure
} from '@totalsoft/pure-validations';
import i18next from 'i18next';
import { Success, Validator, Failure, ValidationError } from '@totalsoft/pure-validations';
import { groupBy } from 'utils/functions';
import { isValidDateFormat } from 'utils/simple-validations';

export const uniqueCollection = (whereFn, groupByFn, property, msg) => {
  return Validator(list => {
    if (list === null || list === undefined) {
      return Success;
    }
    let duplicatedItems = '';

    let items = list.map(whereFn);
    let mergedItems = [];
    for (var index in items) {
      mergedItems = mergedItems.concat(items[index]);
    }
    var groupByList = groupBy(mergedItems, groupByFn);

    var hasDuplicates = groupByList.some(x => x.length > 1);

    if (!hasDuplicates) return Success;

    groupByList.forEach(item => {
      if (item.length > 1) duplicatedItems = duplicatedItems + item[0][property] + '; ';
    });
    if (duplicatedItems !== '') return Failure(ValidationError(i18next.t(msg, { duplicatedItems })));
    else return Success;
  });
};

export const engineScoringHeaderValidator = fromModel(
  model =>
    shape({
      name: required,
      code: required,
      validFrom: [required, lessThan(model.validTo) |> when(Boolean(model.validTo)), isValidDateFormat] |> stopOnFirstFailure,
      validTo: [greaterThan(model.validFrom) |> when(Boolean(model.validFrom)), isValidDateFormat] |> stopOnFirstFailure,
      filterTypeId: required,
      partnerTypeFormula: fromRoot(root => required |> when(root.filterTypeId === 4)),
      partnerTypes: fromRoot(
        root => [atLeastOne, unique(x => x.partnerTypeId), required |> items] |> concatFailure |> when(root.filterTypeId === 1)
      ),
      partnerBaseTypes: fromRoot(
        root => [atLeastOne, unique(x => x.partnerBaseTypeId), required |> items] |> concatFailure |> when(root.filterTypeId === 2)
      )
    }) |> logTo(console)
);

export const engineScoringClassificationsValidator =
  concatFailure(
    unique(x => x.orderNo || {}, 'EngineScoring.EngineClassifications.Validators.OrderNoUnique'),
    unique(x => x.engineScoringClassificationName || {}, 'EngineScoring.EngineClassifications.Validators.NameUnique'),
    uniqueCollection(
      x => x.categories,
      x => x.scoringId,
      'scoringName',
      'EngineScoring.EngineClassifications.EngineCategory.Validators.CategoryNameUnique'
    ),
    uniqueCollection(
      x => x.attributes,
      x => x.engineAttributeId,
      'description',
      'EngineScoring.EngineClassifications.EngineAttribute.Validators.AttributeNameUnique'
    ),
    uniqueCollection(
      x => x.financialIndicators,
      x => x.financialIndicatorId,
      'financialIndicatorName',
      'EngineScoring.EngineClassifications.FinancialIndicator.Validators.FinancialIndicatorNameUnique'
    ),
    items(
      shape({
        engineScoringClassificationName: required,
        orderNo: required,
        attributes: concatFailure(
          unique(x => x.orderNo || {}, 'EngineScoring.EngineClassifications.EngineAttribute.Validators.OrderNoUnique'),
          items(
            shape({
              orderNo: required,
              engineAttributeId: required
            })
          )
        ),
        categories: concatFailure(
          unique(x => x.orderNo || {}, 'EngineScoring.EngineClassifications.EngineCategory.Validators.OrderNoUnique'),
          items(
            shape({
              orderNo: required,
              scoringId: required
            })
          )
        ),
        financialIndicators: concatFailure(
          unique(x => x.orderNo || {}, 'EngineScoring.EngineClassifications.FinancialIndicator.Validators.OrderNoUnique'),
          items(
            shape({
              orderNo: required,
              financialIndicatorId: required
            })
          )
        )
      })
    )
  ) |> logTo(console);

export const engineScoringIntervalsValidator =
  concatFailure(
    unique(x => x.resultId || {}, 'EngineScoring.EngineInterval.Validators.ResultUnique'),
    unique(x => x.startsWith || {}, 'EngineScoring.EngineInterval.Validators.StartsWithUnique'),
    unique(x => x.endsWith || {}, 'EngineScoring.EngineInterval.Validators.EndsWithUnique'),
    items(
      fromModel(model =>
        shape({
          startsWith: required,
          endsWith: [required, greaterThan(model.startsWith) |> when(Boolean(model.startsWith))] |> concatFailure,
          resultId: required
        })
      )
    )
  ) |> logTo(console);

export const engineScoringRulesValidator =
  concatFailure(
    unique(x => x.number || {}, 'EngineScoring.EngineRule.Validators.NumberUnique'),
    unique(x => x.name || {}, 'EngineScoring.EngineRule.Validators.NameUnique'),
    items(
      shape({
        number: required,
        name: required,
        expression: required
      })
    )
  ) |> logTo(console);

export const engineScoringKnockoutCriteriaValidator =
  concatFailure(
    unique(x => x.criteriaId || {}, 'EngineScoring.EngineScoringKnockoutCriteria.Validators.CriterionNameUnique'),
    items(
      shape({
        criteriaId: required
      })
    )
  ) |> logTo(console);

export const engineScoringExternalSystemsValidator =
  concatFailure(
    unique(x => x.externalSystemId || {}, 'EngineScoring.EngineScoringXExternalSystems.Validators.InterrogationUnique'),
    items(
      shape({
        externalSystemId: required
      })
    )
  ) |> logTo(console);
